/**
 * Standalone Milestones & Phases — types for Standard-tier project tracking.
 *
 * These types back the unified milestone/phase system that works in both tiers:
 *   - Standard: manually created/edited milestones and phases
 *   - Professional: Gantt-sourced milestones/phases merge in alongside manual entries
 *
 * Status is derived, not stored:
 *   - Milestone: met (actual_date exists), overdue (target_date past, no actual_date), upcoming
 *   - Phase: complete (pct >= 100), critical (end past + pct < 100), onTrack, notStarted
 */

// ── Milestones ──────────────────────────────────────────────

export type MilestoneSource = 'manual' | 'gantt';

export interface ProjectMilestone {
  milestone_guid: string;
  project_guid: string;
  name: string;
  target_date: string | null;   // YYYY-MM-DD — when the milestone should be met
  actual_date: string | null;   // YYYY-MM-DD — when actually met (null = not yet)
  sort_order: number;
  source: MilestoneSource;
  gantt_task_id: number | null;  // links to GanttTask.taskId in Professional tier
}

export type MilestoneStatus = 'met' | 'overdue' | 'upcoming';

export function deriveMilestoneStatus(m: ProjectMilestone): MilestoneStatus {
  if (m.actual_date) return 'met';
  if (m.target_date) {
    const today = new Date().toISOString().slice(0, 10);
    if (m.target_date < today) return 'overdue';
  }
  return 'upcoming';
}

// ── Phases ──────────────────────────────────────────────────

export type PhaseSource = 'manual' | 'gantt';

export interface ProjectPhase {
  phase_guid: string;
  project_guid: string;
  name: string;
  start_date: string | null;    // YYYY-MM-DD
  end_date: string | null;      // YYYY-MM-DD
  percent_complete: number;      // 0–100
  sort_order: number;
  source: PhaseSource;
  gantt_task_id: number | null;  // links to GanttTask.taskId in Professional tier
}

// ── GUID generation ─────────────────────────────────────────

/** Generate a simple pseudo-GUID for client-side creation. */
export function newGuid(): string {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}
